// 🔹 React Core
import { useMemo, useState, useEffect } from 'react';

// 🔹 Store
import {
  Exercise,
  ExerciseMetric,
  ExerciseType,
  getClosestExerciseImage,
  getDefaultMetric,
  getSupportedMetrics,
  mockExercises,
} from './mockData';
import { trainingStore } from 'src/store/trainingStore';
import { programStore, setStoredWeeks } from 'src/store/programStore';

export interface PlacedExercise extends Exercise {
  placedId: string;
  restBetweenSets?: string;
  restAfterExercise?: string;
}

export type DaySlotType = ExerciseType;

// Record for a day containing arrays of PlacedExercises
export interface ProgramDay {
  [key: string]: PlacedExercise[]; // Key is DaySlotType
}

export interface ProgramWeek {
  _id: string; // e.g., 'Week 1'
  isOpen: boolean;
  selectedDays: string[]; // e.g., ['SAT', 'TUE']
  daysData: Record<string, ProgramDay>; // Key is Day string
}

export const generateId = () => Math.random().toString(36).substr(2, 9);

const mapApiExercise = (title: string, type: DaySlotType, list: any[], duration?: number) => {
  let ex = mockExercises.find((exercise) => exercise.name.toLowerCase() === title.toLowerCase());
  const supportedMetrics = getSupportedMetrics(title);
  const apiHasReps = Boolean(list && list[0]?.reps);
  const apiHasDuration = Boolean(duration);
  const metricType: ExerciseMetric =
    apiHasDuration && supportedMetrics.includes('duration')
      ? 'duration'
      : apiHasReps && supportedMetrics.includes('reps')
        ? 'reps'
        : getDefaultMetric(title);
  const repsValue = metricType === 'reps' ? (apiHasReps ? list[0].reps : ex?.reps || 12) : '';
  const durationValue =
    metricType === 'duration' ? (apiHasDuration ? `${duration}s` : ex?.duration || '30s') : '';

  if (!ex) {
    ex = {
      _id: `ex_gen_${title.replace(/\\s+/g, '_')}`,
      name: title,
      muscleGroup: 'Full Body',
      type: [type],
      thumbnail: getClosestExerciseImage(title),
      sets: list ? list.length : 1,
      reps: repsValue,
      duration: durationValue,
      metricType,
      supportedMetrics,
      rest: list && list[0] ? list[0].rest : '60s',
      impact: 'Medium imp.',
    };
  }

  return {
    ...ex,
    placedId: generateId(),
    sets: list ? list.length : 1,
    reps: repsValue,
    duration: durationValue,
    metricType,
    supportedMetrics: ex.supportedMetrics || supportedMetrics,
    rest: list && list[0] ? list[0].rest : ex.rest,
  };
};

export const buildProgramWeeks = (
  initialType: string | undefined,
  apiProgramData: any,
  formattedDays: string[],
  maxColumns: number = 3,
): ProgramWeek[] => {
  if (initialType === 'ai' && apiProgramData && apiProgramData.datetimeList) {
    const weeksArray: ProgramWeek[] = [];
    const sortedDays = [...apiProgramData.datetimeList].sort(
      (a, b) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
    );

    let currentWeekId = 1;
    let weekDaysData: Record<string, ProgramDay> = {};
    let weekSelectedDays: string[] = [];
    let weekStartDate = sortedDays.length > 0 ? new Date(sortedDays[0].ymd) : new Date();

    sortedDays.forEach((dayData: any) => {
      const currentDate = new Date(dayData.ymd);
      const diffTime = Math.abs(currentDate.getTime() - weekStartDate.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays >= 7) {
        weeksArray.push({
          _id: `Week ${currentWeekId}`,
          isOpen: currentWeekId === 1,
          selectedDays: [...new Set(weekSelectedDays)].slice(0, maxColumns),
          daysData: weekDaysData,
        });
        currentWeekId++;
        weekDaysData = {};
        weekSelectedDays = [];
        weekStartDate = currentDate;
      }

      const dayStr = (dayData.weekday2 || dayData.weekday.substring(0, 3)).toUpperCase();
      weekSelectedDays.push(dayStr);
      weekDaysData[dayStr] = { Warmup: [], Main: [], Cooldown: [] };

      if (dayData.warmUpMoves) {
        dayData.warmUpMoves.forEach((move: any) => {
          weekDaysData[dayStr]['Warmup'].push(
            mapApiExercise(move.title, 'Warmup', [], move.duration),
          );
        });
      }
      if (dayData.moves) {
        dayData.moves.forEach((move: any) => {
          weekDaysData[dayStr]['Main'].push(mapApiExercise(move.title, 'Main', move.list));
        });
      }
      if (dayData.coolDownMoves) {
        dayData.coolDownMoves.forEach((move: any) => {
          weekDaysData[dayStr]['Cooldown'].push(
            mapApiExercise(move.title, 'Cooldown', [], move.duration),
          );
        });
      }
    });

    if (weekSelectedDays.length > 0) {
      weeksArray.push({
        _id: `Week ${currentWeekId}`,
        isOpen: currentWeekId === 1,
        selectedDays: [...new Set(weekSelectedDays)].slice(0, maxColumns),
        daysData: weekDaysData,
      });
    }

    const targetDuration = parseInt(apiProgramData.duration || '1', 10);
    while (weeksArray.length < targetDuration) {
      currentWeekId++;
      const lastWeek = weeksArray[weeksArray.length - 1];

      const clonedDaysData: Record<string, ProgramDay> = {};
      Object.keys(lastWeek.daysData).forEach((day) => {
        clonedDaysData[day] = {};
        Object.keys(lastWeek.daysData[day]).forEach((slot) => {
          clonedDaysData[day][slot as DaySlotType] = lastWeek.daysData[day][slot].map((ex) => ({
            ...ex,
            placedId: generateId(),
          }));
        });
      });

      weeksArray.push({
        _id: `Week ${currentWeekId}`,
        isOpen: false,
        selectedDays: [...lastWeek.selectedDays],
        daysData: clonedDaysData,
      });
    }

    return weeksArray;
  }

  return Array.from({ length: 6 }).map((_, index) => {
    const initialDaysData: Record<string, ProgramDay> = {};
    formattedDays.forEach((day: string) => {
      initialDaysData[day] = {};
    });

    const defaultSelectedDays = formattedDays.slice(0, maxColumns);

    return {
      _id: `Week ${index + 1}`,
      isOpen: index === 0,
      selectedDays: [...defaultSelectedDays],
      daysData: initialDaysData,
    };
  });
};

export const useProgramStore = (
  initialType?: string,
  apiProgramData?: any,
  skipStoredWeeks = false,
) => {
  // Store
  const assessment = trainingStore((state) => state.assessment || {});

  // Library State
  const [exercises, setExercises] = useState<Exercise[]>(mockExercises);
  const [search, setSearch] = useState('');
  const [muscleFilter, setMuscleFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Drag State
  const [draggedExercise, setDraggedExercise] = useState<Exercise | null>(null);
  const [draggedOverSlot, setDraggedOverSlot] = useState<{
    weekId: string;
    day: string;
    slot: DaySlotType;
  } | null>(null);

  // Reorder dragging state (within library)
  const [draggedLibIndex, setDraggedLibIndex] = useState<number | null>(null);
  const [draggedLibOverIndex, setDraggedLibOverIndex] = useState<number | null>(null);

  // Clipboard State
  const [clipboard, setClipboard] = useState<{
    exercises: PlacedExercise[];
    type: DaySlotType;
  } | null>(null);

  const formattedDays = assessment?.selectedDays
    ? assessment.selectedDays.map((day: string) => day.toUpperCase())
    : ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const [maxColumns, setMaxColumns] = useState(() => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1200) return 4;
      if (window.innerWidth >= 768) return 3;
      return 1;
    }
    return 3;
  });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) setMaxColumns(4);
      else if (window.innerWidth >= 768) setMaxColumns(3);
      else setMaxColumns(1);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Program State
  const [allWeeksSame, setAllWeeksSame] = useState(false);
  const [isRemixModalOpen, setRemixModalOpen] = useState(false);
  const [isAiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [weeks, setWeeks] = useState<ProgramWeek[]>(() => {
    // Back-navigation from /completed: restore the trainer's last-saved week/exercise
    // state (including any drag-and-drop additions) from the Zustand store.
    // skipStoredWeeks is true on a fresh generation so we always rebuild from the API.
    if (!skipStoredWeeks) {
      const stored = programStore.getState().weeks;
      if (stored && stored.length > 0) return stored as ProgramWeek[];
    }

    const formattedDays = assessment?.selectedDays
      ? assessment.selectedDays.map((d: string) => d.toUpperCase())
      : ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    const initialMaxCols =
      typeof window !== 'undefined'
        ? window.innerWidth >= 1200
          ? 4
          : window.innerWidth >= 768
            ? 3
            : 1
        : 3;

    if (initialType === 'ai' && apiProgramData && apiProgramData.datetimeList) {
      const weeksArray: ProgramWeek[] = [];
      const sortedDays = [...apiProgramData.datetimeList].sort(
        (a, b) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
      );

      let currentWeekId = 1;
      let weekDaysData: Record<string, ProgramDay> = {};
      let weekSelectedDays: string[] = [];
      let weekStartDate = sortedDays.length > 0 ? new Date(sortedDays[0].ymd) : new Date();

      sortedDays.forEach((dayData: any) => {
        const currentDate = new Date(dayData.ymd);
        const diffTime = Math.abs(currentDate.getTime() - weekStartDate.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays >= 7) {
          weeksArray.push({
            _id: `Week ${currentWeekId}`,
            isOpen: currentWeekId === 1,
            selectedDays: [...new Set(weekSelectedDays)].slice(0, initialMaxCols),
            daysData: weekDaysData,
          });
          currentWeekId++;
          weekDaysData = {};
          weekSelectedDays = [];
          weekStartDate = currentDate;
        }

        const dayStr = (dayData.weekday2 || dayData.weekday.substring(0, 3)).toUpperCase();
        weekSelectedDays.push(dayStr);
        weekDaysData[dayStr] = { Warmup: [], Main: [], Cooldown: [] };

        const mapExercise = (title: string, type: DaySlotType, list: any[], duration?: number) => {
          let ex = mockExercises.find((e) => e.name.toLowerCase() === title.toLowerCase());
          const supportedMetrics = getSupportedMetrics(title);
          const apiHasReps = Boolean(list && list[0]?.reps);
          const apiHasDuration = Boolean(duration);
          const metricType: ExerciseMetric =
            apiHasDuration && supportedMetrics.includes('duration')
              ? 'duration'
              : apiHasReps && supportedMetrics.includes('reps')
                ? 'reps'
                : getDefaultMetric(title);
          const repsValue =
            metricType === 'reps' ? (apiHasReps ? list[0].reps : ex?.reps || 12) : '';
          const durationValue =
            metricType === 'duration'
              ? apiHasDuration
                ? `${duration}s`
                : ex?.duration || '30s'
              : '';

          if (!ex) {
            ex = {
              _id: `ex_gen_${title.replace(/\\s+/g, '_')}`,
              name: title,
              muscleGroup: 'Full Body',
              type: [type],
              thumbnail: getClosestExerciseImage(title),
              sets: list ? list.length : 1,
              reps: repsValue,
              duration: durationValue,
              metricType,
              supportedMetrics,
              rest: list && list[0] ? list[0].rest : '60s',
              impact: 'Medium imp.',
            };
          }
          return {
            ...ex,
            placedId: generateId(),
            sets: list ? list.length : 1,
            reps: repsValue,
            duration: durationValue,
            metricType,
            supportedMetrics: ex.supportedMetrics || supportedMetrics,
            rest: list && list[0] ? list[0].rest : ex.rest,
          };
        };

        if (dayData.warmUpMoves) {
          dayData.warmUpMoves.forEach((move: any) => {
            weekDaysData[dayStr]['Warmup'].push(
              mapExercise(move.title, 'Warmup', [], move.duration),
            );
          });
        }
        if (dayData.moves) {
          dayData.moves.forEach((move: any) => {
            weekDaysData[dayStr]['Main'].push(mapExercise(move.title, 'Main', move.list));
          });
        }
        if (dayData.coolDownMoves) {
          dayData.coolDownMoves.forEach((move: any) => {
            weekDaysData[dayStr]['Cooldown'].push(
              mapExercise(move.title, 'Cooldown', [], move.duration),
            );
          });
        }
      });

      if (weekSelectedDays.length > 0) {
        weeksArray.push({
          _id: `Week ${currentWeekId}`,
          isOpen: currentWeekId === 1,
          selectedDays: [...new Set(weekSelectedDays)].slice(0, initialMaxCols),
          daysData: weekDaysData,
        });
      }

      const targetDuration = parseInt(apiProgramData.duration || '1', 10);
      while (weeksArray.length < targetDuration) {
        currentWeekId++;
        const lastWeek = weeksArray[weeksArray.length - 1];

        const clonedDaysData: Record<string, ProgramDay> = {};
        Object.keys(lastWeek.daysData).forEach((day) => {
          clonedDaysData[day] = {};
          Object.keys(lastWeek.daysData[day]).forEach((slot) => {
            clonedDaysData[day][slot as DaySlotType] = lastWeek.daysData[day][slot].map((ex) => ({
              ...ex,
              placedId: generateId(),
            }));
          });
        });

        weeksArray.push({
          _id: `Week ${currentWeekId}`,
          isOpen: false,
          selectedDays: [...lastWeek.selectedDays],
          daysData: clonedDaysData,
        });
      }

      return weeksArray;
    }

    return Array.from({ length: 6 }).map((_, i) => {
      const initialDaysData: Record<string, ProgramDay> = {};
      formattedDays.forEach((day: string) => {
        initialDaysData[day] = {};

        // 🔹 AI DATA INITIALIZATION (fallback)
        // if (initialType === 'ai' && i === 0 && !apiProgramData) {
        //   // "exercise first 2 type" -> Warmup (index 0) and Main (index 1) in EXERCISE_TYPES (excl 'all')
        //   const typesToFill = ['Warmup', 'Main'];
        //
        //   typesToFill.forEach((t) => {
        //     const matches = mockExercises.filter((ex) => ex.type.includes(t as any));
        //     if (matches.length > 0) {
        //       // Pick 3 random exercises if available
        //       const shuffled = [...matches].sort(() => 0.5 - Math.random());
        //       const selected = shuffled.slice(0, 3);
        //
        //       initialDaysData[day][t] = selected.map((ex) => ({
        //         ...ex,
        //         placedId: generateId(),
        //       }));
        //     }
        //   });
        // }
      });

      const defaultSelectedDays = formattedDays.slice(0, initialMaxCols);

      return {
        _id: `Week ${i + 1}`,
        isOpen: i === 0, // Week 1 open by default
        selectedDays: [...defaultSelectedDays],
        daysData: initialDaysData,
      };
    });
  });

  const resetProgramData = (nextApiProgramData: any) => {
    setWeeks(buildProgramWeeks(initialType, nextApiProgramData, formattedDays, maxColumns));
    setClipboard(null);
    setAllWeeksSame(false);
    setDraggedExercise(null);
    setDraggedOverSlot(null);
    setRemixModalOpen(false);
  };

  // Filtered Exercises Output (Alphabetical grouped)
  const filteredExercises = useMemo(() => {
    let sorted = [...exercises];
    if (search.trim()) {
      sorted = sorted.filter((ex) => ex.name.toLowerCase().includes(search.toLowerCase()));
    }
    if (muscleFilter !== 'all') {
      sorted = sorted.filter((ex) => {
        const mg = ex.muscleGroup.toLowerCase().replace(' ', '-');
        return mg === muscleFilter || mg === 'full-body';
      });
    }
    if (typeFilter !== 'all') {
      sorted = sorted.filter((ex) => ex.type.some((t) => t.toLowerCase() === typeFilter));
    }

    // Group by first letter
    const grouped: Record<string, Exercise[]> = {};
    sorted.forEach((ex) => {
      const letter = ex.name.charAt(0).toUpperCase();
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(ex);
    });
    return grouped;
  }, [exercises, search, muscleFilter, typeFilter]);

  const toggleWeek = (weekId: string, forceState?: boolean) => {
    setWeeks((ws) =>
      ws.map((w, index) => {
        if (weekId === 'All weeks' && index === 0) {
          return { ...w, isOpen: forceState !== undefined ? forceState : !w.isOpen };
        }
        if (w._id === weekId) {
          return { ...w, isOpen: forceState !== undefined ? forceState : !w.isOpen };
        }
        return { ...w, isOpen: false };
      }),
    );
  };

  const deleteWeek = (weekId: string) => {
    setWeeks((ws) => ws.filter((w) => w._id !== weekId));
  };

  const toggleDaySelection = (weekId: string, dayStr: string) => {
    setWeeks((ws) =>
      ws.map((w) => {
        if (allWeeksSame || w._id === weekId) {
          let newSelected = [...w.selectedDays];
          if (newSelected.includes(dayStr)) {
            newSelected = newSelected.filter((d) => d !== dayStr);
          } else {
            // Check max columns limit and remove oldest if needed
            if (newSelected.length >= maxColumns) {
              newSelected.shift(); // Uncheck the first/oldest selected day
            }
            newSelected.push(dayStr);
            // Ensure dayData exists
            if (!w.daysData[dayStr]) {
              w.daysData[dayStr] = {};
            }
          }
          return { ...w, selectedDays: newSelected };
        }
        return w;
      }),
    );
  };

  const placeExercise = (weekId: string, day: string, slot: DaySlotType, exercise: Exercise) => {
    const placedEntry: PlacedExercise = { ...exercise, placedId: generateId() };

    setWeeks((ws) =>
      ws.map((w) => {
        // If allWeeksSame is checked, apply to ALL weeks if they have that day
        if (allWeeksSame || w._id === weekId) {
          // Ensure the day exists
          if (!w.daysData[day]) {
            w.daysData[day] = {};
          }
          return {
            ...w,
            daysData: {
              ...w.daysData,
              [day]: {
                ...w.daysData[day],
                [slot]: [
                  ...(w.daysData[day][slot] || []),
                  { ...placedEntry, placedId: generateId() },
                ], // New ID per instance
              },
            },
          };
        }
        return w;
      }),
    );
  };

  const removePlacedExercise = (
    weekId: string,
    day: string,
    slot: DaySlotType,
    placedId: string,
  ) => {
    setWeeks((ws) =>
      ws.map((w) => {
        if (allWeeksSame || w._id === weekId) {
          if (!w.daysData[day]) return w;
          return {
            ...w,
            daysData: {
              ...w.daysData,
              [day]: {
                ...w.daysData[day],
                [slot]: (w.daysData[day][slot] || []).filter((e) => e.placedId !== placedId),
              },
            },
          };
        }
        return w;
      }),
    );
  };

  const updatePlacedExercise = (
    weekId: string,
    day: string,
    slot: DaySlotType,
    placedId: string,
    updatedData: Partial<PlacedExercise>,
  ) => {
    setWeeks((ws) =>
      ws.map((w) => {
        if (allWeeksSame || w._id === weekId) {
          if (!w.daysData[day] || !w.daysData[day][slot]) return w;
          return {
            ...w,
            daysData: {
              ...w.daysData,
              [day]: {
                ...w.daysData[day],
                [slot]: (w.daysData[day][slot] || []).map((ex) =>
                  ex.placedId === placedId ? { ...ex, ...updatedData } : ex,
                ),
              },
            },
          };
        }
        return w;
      }),
    );
  };
  const copySlot = (exercises: PlacedExercise[], slotType: DaySlotType) => {
    setClipboard({ exercises: [...exercises], type: slotType });
  };

  const emptySlot = (weekId: string, day: string, slot: DaySlotType) => {
    setWeeks((ws) =>
      ws.map((w) => {
        if (allWeeksSame || w._id === weekId) {
          if (!w.daysData[day]) return w;
          return {
            ...w,
            daysData: {
              ...w.daysData,
              [day]: {
                ...w.daysData[day],
                [slot]: [],
              },
            },
          };
        }
        return w;
      }),
    );
  };

  const pasteSlot = (weekId: string, day: string, slot: DaySlotType) => {
    if (!clipboard) return false;
    if (clipboard.type.toLowerCase() !== slot.toLowerCase()) return false;

    const newEx = clipboard.exercises.map((ex) => ({
      ...ex,
      placedId: generateId(),
    }));

    setWeeks((ws) =>
      ws.map((w) => {
        if (allWeeksSame || w._id === weekId) {
          return {
            ...w,
            daysData: {
              ...w.daysData,
              [day]: {
                ...w.daysData[day],
                [slot]: [...(w.daysData[day][slot] || []), ...newEx],
              },
            },
          };
        }
        return w;
      }),
    );
    return true;
  };

  const totalExercisesPlaced = useMemo(() => {
    let count = 0;
    weeks.forEach((w) => {
      Object.keys(w.daysData).forEach((day) => {
        Object.keys(w.daysData[day]).forEach((slot) => {
          count += (w.daysData[day][slot] || []).length;
        });
      });
    });
    return count;
  }, [weeks]);

  // Sync weeks to the Zustand programStore on every change.
  // This covers: initial API load, every drag-and-drop, every edit, and remix.
  // The stored value is consumed by the useState initializer above on back-navigation.
  useEffect(() => {
    setStoredWeeks(weeks);
  }, [weeks]);

  const reorderLibrary = (letterKey: string, sourceIndex: number, destinationIndex: number) => {
    setExercises((prevEx) => {
      const justLetter = prevEx.filter((ex) => ex.name.charAt(0).toUpperCase() === letterKey);
      const others = prevEx.filter((ex) => ex.name.charAt(0).toUpperCase() !== letterKey);

      const [removed] = justLetter.splice(sourceIndex, 1);
      justLetter.splice(destinationIndex, 0, removed);

      return [...others, ...justLetter];
    });
  };

  return {
    // state
    exercises,
    search,
    setSearch,
    muscleFilter,
    setMuscleFilter,
    typeFilter,
    setTypeFilter,
    filteredExercises,
    formattedDays,

    draggedExercise,
    setDraggedExercise,
    draggedOverSlot,
    setDraggedOverSlot,

    draggedLibIndex,
    setDraggedLibIndex,
    draggedLibOverIndex,
    setDraggedLibOverIndex,

    allWeeksSame,
    setAllWeeksSame,
    isRemixModalOpen,
    setRemixModalOpen,
    isAiAssistantOpen,
    setAiAssistantOpen,
    weeks,

    // actions
    toggleWeek,
    deleteWeek,
    toggleDaySelection,
    placeExercise,
    removePlacedExercise,
    updatePlacedExercise,
    copySlot,
    emptySlot,
    pasteSlot,
    clipboard,
    reorderLibrary,
    resetProgramData,
    totalExercisesPlaced,
  };
};
